import { useContext, useEffect } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { Loading } from "@/shared/components/Loading/Loading";
import WelcomeElement from "@/shared/components/candidate/scenes/WelcomeElement/WelcomeElement";
import useAllTextLocations from "@/shared/components/assessment/AboutAssessment/hooks/useAllTextLocations";
import { UserContext } from "@/shared/contexts/user.context";
import { Button } from "@/ui-components/commonComponents/Button/Button";
import {
  ASSESSMENT_ID_SEARCH,
  ASSESSMENT_ROUTE,
  CANDIDATE_ROUTE,
  LOGIN_ROUTE,
  SELF_REGISTER_ROUTE,
} from "@/routing/AppRouter/routes.constants";
import { DEFAULT_LOCALE } from "@/i18n";
import { getTestsProps } from "../../utils/candidateAssessment.utils";
import CandidateAssessmentDesktop from "./CandidateAssessmentDesktop";
import CandidateAssessmentMobile from "./CandidateAssessmentMobile";
import { getRouteWithSearchParameter } from "@/shared/utils/route/route.utils";
import { CandidateAssessmentViewTypes } from "@/modules/candidate/assessment/CandidateAssessment/CandidateAssessment";
import { getMainButtonTitleMessage } from "./utils/candidate-assessment-view.utils";
import { useGetAssessment } from "@/modules/candidate/hooks/useGetAssessment.hook";

export default function CandidateAssessmentSelfRegister() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { assessmentId } = useParams();
  const isAuthenticated: boolean = Boolean(user && user.userId);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(`/${ASSESSMENT_ROUTE}/${assessmentId}/${SELF_REGISTER_ROUTE}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { assessment, isLoading } = useGetAssessment({ assessmentId, locale: DEFAULT_LOCALE });
  const { xxl, xl, lg } = useBreakpoint();
  const isDesktopView = xxl || xl || lg;
  const { textLocations, isLoading: textLocationsLoading } = useAllTextLocations();
  const intl = useIntl();
  const { imageUrl, title, leftTime, introText } = getTestsProps({ assessment, textLocations, intl });
  const buttonTitle = intl.formatMessage(
    getMainButtonTitleMessage({ isAuthenticated: false, isSurvey: Boolean(assessment?.isSurvey) }),
  );
  const viewType = CandidateAssessmentViewTypes.start;

  const handleSubmitClick = () => {
    return navigate(
      getRouteWithSearchParameter(`/${CANDIDATE_ROUTE}/${LOGIN_ROUTE}`, ASSESSMENT_ID_SEARCH, String(assessmentId)),
    );
  };

  if (isLoading || textLocationsLoading) {
    return <Loading />;
  }

  return (
    <div>
      {isDesktopView && (
        <CandidateAssessmentDesktop
          assessmentImageUrl={imageUrl}
          viewType={viewType}
          clientLogo={assessment?.client?.logoUrl}
          clientName={assessment?.client?.name}
        >
          <WelcomeElement
            type={viewType}
            title={title}
            description={introText}
            leftTime={leftTime}
            footer={isDesktopView ? <Button onClick={handleSubmitClick}>{buttonTitle}</Button> : null}
          />
        </CandidateAssessmentDesktop>
      )}

      {!isDesktopView && (
        <CandidateAssessmentMobile
          clientLogo={assessment?.client?.logoUrl}
          clientName={assessment?.client?.name}
          assessmentImageUrl={imageUrl}
          viewType={viewType}
          childrenSubmitButton={
            <Button onClick={handleSubmitClick} fullWidth={true}>
              {buttonTitle}
            </Button>
          }
        >
          <WelcomeElement type={viewType} title={title} description={introText} leftTime={leftTime} />
        </CandidateAssessmentMobile>
      )}
    </div>
  );
}
